import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getProducts } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { PRODUCT_DEFAULT_IMAGE } from '../../medias/public';
import {
  defaultProductFilter,
  productInputLabel,
  productOptionLabel,
  filterProductOptions,
} from '../private/productSkuPickerUtils';
import { fetchProductImageMap, productImageUrlFromMap } from '../private/productImageUtils';

export function ProductSearchAutocomplete({
  productId = null,
  value = null,
  onChange,
  disabled = false,
  productFilter,
  productTypeFilter = null,
  size = 'small',
  sx,
  label,
  placeholder,
  showThumbnail = true,
}) {
  const { translate } = useTranslation();

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productImages, setProductImages] = useState({});
  const productsLoadedRef = useRef(false);
  const productsRef = useRef([]);

  const [selectedProduct, setSelectedProduct] = useState(value ?? null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const resolvedProductFilter = useMemo(() => {
    if (productFilter) return productFilter;
    if (productTypeFilter) {
      const typeUpper = String(productTypeFilter).toUpperCase();
      return (p) =>
        defaultProductFilter(p) && (p.type || 'PRODUCT').toUpperCase() === typeUpper;
    }
    return defaultProductFilter;
  }, [productFilter, productTypeFilter]);

  const loadProductImages = useCallback(async (productList) => {
    if (!showThumbnail) return;
    const ids = (productList || []).map((p) => p.id).filter(Boolean);
    const map = await fetchProductImageMap(ids.length ? ids : undefined);
    setProductImages(map);
  }, [showThumbnail]);

  const loadProducts = useCallback(async () => {
    if (productsLoadedRef.current) return productsRef.current;
    setProductsLoading(true);
    try {
      const res = await getProducts();
      const list = res.data?.products || res?.data || [];
      const arr = Array.isArray(list) ? list : [];
      productsRef.current = arr;
      productsLoadedRef.current = true;
      setProducts(arr);
      await loadProductImages(arr);
      return arr;
    } finally {
      setProductsLoading(false);
    }
  }, [loadProductImages]);

  const syncInputFromSelection = useCallback(() => {
    setInputValue(selectedProduct ? productInputLabel(selectedProduct) : '');
  }, [selectedProduct]);

  useEffect(() => {
    if (!open) syncInputFromSelection();
  }, [open, syncInputFromSelection]);

  useEffect(() => {
    if (value != null) {
      setSelectedProduct(value);
      if (!open) {
        setInputValue(value ? productInputLabel(value) : '');
      }
      return;
    }

    if (productId === undefined) return;

    if (!productId) {
      setSelectedProduct(null);
      if (!open) setInputValue('');
      return;
    }

    let cancelled = false;
    (async () => {
      const list = await loadProducts();
      if (cancelled) return;
      const product = list.find((p) => p.id === productId);
      if (!product || !resolvedProductFilter(product)) return;
      setSelectedProduct(product);
      if (!open) setInputValue(productInputLabel(product));
    })();

    return () => {
      cancelled = true;
    };
  }, [productId, value, loadProducts, resolvedProductFilter, open]);

  const productOptions = useMemo(
    () => products.filter(resolvedProductFilter),
    [products, resolvedProductFilter],
  );

  const handleChange = useCallback(
    (_, product, reason) => {
      setSelectedProduct(product ?? null);
      if (reason === 'selectOption' || reason === 'clear') {
        setOpen(false);
        setInputValue(product ? productInputLabel(product) : '');
      }
      onChange?.(product ?? null);
    },
    [onChange],
  );

  const handleOpen = useCallback(() => {
    void loadProducts();
    setOpen(true);
    setInputValue('');
  }, [loadProducts]);

  const handleClose = useCallback(() => {
    setOpen(false);
    syncInputFromSelection();
  }, [syncInputFromSelection]);

  const resolvedPlaceholder =
    placeholder || translate('products.sku_picker_product_placeholder');

  const selectedThumbnailUrl = selectedProduct
    ? productImageUrlFromMap(selectedProduct.id, productImages)
    : null;

  return (
    <Autocomplete
      size={size}
      disabled={disabled}
      open={open}
      options={productOptions}
      loading={productsLoading}
      value={selectedProduct}
      inputValue={inputValue}
      onChange={handleChange}
      onOpen={handleOpen}
      onClose={handleClose}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'input') setInputValue(newValue);
      }}
      filterOptions={filterProductOptions}
      getOptionLabel={productInputLabel}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      blurOnSelect={false}
      clearOnBlur={false}
      openOnFocus
      autoHighlight
      handleHomeEndKeys
      sx={sx}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const thumbUrl = showThumbnail
          ? productImageUrlFromMap(option.id, productImages)
          : PRODUCT_DEFAULT_IMAGE;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}
          >
            {showThumbnail ? (
              <Avatar
                src={thumbUrl}
                alt=""
                variant="rounded"
                sx={{ width: 36, height: 36, flexShrink: 0 }}
              />
            ) : null}
            <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem' }}>
              {productOptionLabel(option)}
            </Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={label ? undefined : resolvedPlaceholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': label || resolvedPlaceholder,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment:
              showThumbnail && selectedThumbnailUrl ? (
                <>
                  <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.5 }}>
                    <Avatar
                      src={selectedThumbnailUrl}
                      alt=""
                      variant="rounded"
                      sx={{ width: 28, height: 28 }}
                    />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
            endAdornment: (
              <>
                {productsLoading ? <CircularProgress color="inherit" size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ProductSearchAutocomplete;
